import type AwcAutocompleteElement from '@ambiki/impulse-view-components/dist/elements/autocomplete';
import axios from 'axios';
import { nextFrame } from 'helpers/timing';
import isEqual from 'lodash.isequal';
import RecurringSelect, { RecurringHash } from '../lib/recurring-select';

interface ServerResponse {
  message: string;
}

export default class RecurringSelectElement extends HTMLElement {
  select: AwcAutocompleteElement | null = null;
  errorField: HTMLElement | null = null;
  modal: HTMLElement | null = null;
  recurringSelect: RecurringSelect | null = null;

  connectedCallback() {
    this.select = this.querySelector<AwcAutocompleteElement>('awc-autocomplete');
    this.errorField = this.querySelector('[data-recurring-select-error]');
    const modalId = this.getAttribute('data-modal-id');
    if (!this.select || !modalId) return;

    this.modal = document.getElementById(modalId);
    if (!this.modal) return;

    this.recurringSelect = new RecurringSelect(this, this.select, this.modal, this.parsedValue);

    this.openModal = this.openModal.bind(this);
    this.closeModal = this.closeModal.bind(this);

    this.select.addEventListener('awc-autocomplete:commit', this.openModal);
    $(this.modal).on('hidden.bs.modal', this.closeModal);
  }

  disconnectedCallback() {
    this.recurringSelect?.stop();
    this.select?.removeEventListener('awc-autocomplete:commit', this.openModal);

    if (this.modal) {
      $(this.modal).off('hidden.bs.modal', this.closeModal);
    }
  }

  static get observedAttributes(): string[] {
    return ['value'];
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string): void {
    switch (name) {
      case 'value':
        if (newValue !== null) {
          if (!this.modal) return;
          $(this.modal).modal('hide');
        }
        break;
      default:
        break;
    }
  }

  async openModal(event: Event) {
    if (this.errorField) this.errorField.innerText = '';
    const { target } = (event as CustomEvent<{ target: HTMLElement }>).detail;
    const value = target.getAttribute('value') || '';
    if (value !== 'custom') return;

    $(this.modal as HTMLElement).modal('show');
    this.recurringSelect?.start();
    await nextFrame();
    this.select?.removeAttribute('data-focus');
  }

  async closeModal() {
    if (!this.select) return;

    const foundOption = this.select.options.find(getOptionByValue(this.parsedValue));
    if (foundOption) {
      const value = foundOption.getAttribute('value') || '';
      const text = foundOption.getAttribute('data-text') || '';
      this.select.setValue(value, text);
      return;
    }

    await this.validateRecurringHash();
  }

  async validateRecurringHash() {
    if (!this.select || !this.src || !this.parsedValue) return;
    this.select.disabled = true;
    if (this.formSubmitBtn) {
      this.formSubmitBtn.disabled = true;
    }

    try {
      const { data } = await axios.post<ServerResponse>(this.src, { rule: { rule_hash: this.parsedValue } });
      createOption(data.message, JSON.stringify(this.parsedValue), this.select);
      this.select.dispatchEvent(new Event('change'));
    } catch (error) {
      if (this.errorField && axios.isAxiosError(error)) {
        this.errorField.innerText = error.response?.data.message || 'An error occurred. Please set the schedule again.';
      }
      const defaultOption = this.select.options[0];
      if (defaultOption) {
        const value = defaultOption.getAttribute('value') || '';
        const text = defaultOption.getAttribute('data-text') || '';
        this.select.setValue(value, text);
      }
    } finally {
      this.select.disabled = false;
      if (this.formSubmitBtn) {
        this.formSubmitBtn.disabled = false;
      }
    }
  }

  get value() {
    const value = this.getAttribute('value');
    if (!value) return '';
    return value;
  }

  set value(hash: string) {
    this.setAttribute('value', hash);
  }

  get src() {
    return this.getAttribute('src');
  }

  get parsedValue(): null | RecurringHash {
    return JSON.parse(this.value) || '';
  }

  get options() {
    return this.select?.options;
  }

  get formSubmitBtn(): HTMLButtonElement | HTMLInputElement | null {
    const form = this.closest('form');
    if (!form) return null;

    return form.querySelector('input[type="submit"]') || form.querySelector('button[type="submit"]');
  }
}

function createOption(text: string, value: string, select: AwcAutocompleteElement) {
  const option = document.createElement('div');
  option.setAttribute('value', value);
  option.setAttribute('data-text', text);
  option.setAttribute('role', 'option');
  option.setAttribute('data-custom', '');
  option.classList.add('awc-autocomplete-option');
  option.classList.add('dropdown-item');
  const optionText = document.createElement('span');
  optionText.classList.add('awc-autocomplete-option-text');
  optionText.innerText = text;
  option.append(optionText);

  const icon = document.createElement('i');
  icon.classList.add('bi');
  icon.classList.add('bi-check2');
  icon.classList.add('awc-autocomplete-option-check');
  option.append(icon);

  select.optionsContainer.append(option);
  select.setValue(value, text);
}

function getOptionByValue(value: null | RecurringHash) {
  return (option: HTMLElement) => {
    const oValue = option.getAttribute('value') || '';

    try {
      return isEqual(JSON.parse(oValue), value);
    } catch {
      return isEqual(oValue, value);
    }
  };
}
